import { useRef, useState } from 'react';
import { uid } from 'uid';

import { uploadFiles } from '~/shared/helpers/file-helpers';
import { useAppDispatch } from '~/shared/store/hooks';

// TinyMCE/HTML-editor images only: keep rich-text uploads crisp without changing regular file/image uploads.
const HTML_EDITOR_IMAGE_UPLOAD_PARAMS = [{ quality: 0.96, maxWidth: 1600 }];

const useContentImages = ({ localImagesInitialState = {} }) => {
  const dispatch = useAppDispatch();
  const [localImages, setLocalImages] = useState(localImagesInitialState);
  const imageProcessed = useRef(false);

  const getContentWithResizedImages = async ({ uploadParams, fields }) => {
    await Promise.all(
      Object.keys(localImagesInitialState).map(async (item) => {
        await Promise.all(
          Object.entries(localImages[item] ?? {}).map(async ([imageId, blobInfo], i) => {
            const blob = blobInfo?.blob();
            const fieldName = 'content-image';

            function blobToDataURL(blob: Blob): Promise<string> {
              return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (_e) => resolve(reader.result as string);
                reader.onerror = (_e) => reject(reader.error);
                reader.onabort = (_e) => reject(new Error('Read aborted'));
                reader.readAsDataURL(blob);
              });
            }
            const dataUrl = await blobToDataURL(blob);
            const result = await uploadFiles({
              fields: {
                [fieldName]: {
                  path: dataUrl,
                  status: 'new',
                  name: fields?.alias ? `${fields?.alias}-${uid(7)}` : uid(7),
                },
              },
              types: [fieldName],
              dispatch,
              ...uploadParams,
              typeParams: HTML_EDITOR_IMAGE_UPLOAD_PARAMS,
            });
            const data = blobInfo.base64();

            fields[item] = fields[item].replace(`data:image/jpeg;base64,${data}`, result?.[fieldName]?.['0']?.path);
            fields[item] = fields[item].replace(`data:image/png;base64,${data}`, result?.[fieldName]?.['0']?.path);
            fields[item] = fields[item].replace(`data:image/webp;base64,${data}`, result?.[fieldName]?.['0']?.path);
          }),
        );
      }),
    );
    setLocalImages(localImagesInitialState);

    return fields;
  };

  const processedRef = useRef(new Map<string, string>());

  const getImagesUploadHandler = (type) => async (blobInfo) => {
    const fingerprint = [type, blobInfo.blob().size, blobInfo.blob().type, blobInfo.base64?.()?.slice(0, 100)].join(
      '|',
    );

    const existingUrl = processedRef.current.get(fingerprint);
    if (existingUrl) {
      return existingUrl;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const dataUrl = reader.result as string;
        const imageId = Date.now().toString();

        processedRef.current.set(fingerprint, dataUrl);

        setLocalImages((prev) => ({
          ...prev,
          [type]: {
            ...(prev?.[type] ?? {}),
            [imageId]: blobInfo,
          },
        }));

        resolve(dataUrl);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blobInfo.blob());
    });
  };

  return {
    localImages,
    getContentWithResizedImages,
    getImagesUploadHandler,
    setLocalImages,
  };
};

export default useContentImages;
